import { FRAMES, type Size } from "odyssey-shared";
import { KIT_METRICS, centreOffset } from "../../ui/kit/metrics.ts";
import { windowRoom } from "../../ui/kit/place.ts";
import { mapWindowMost } from "./nearby.ts";

/**
 * The world map window's layout (ADR-100 decision 1 and its note of 2026-10-14): 480 by 300 logical pixels, centred,
 * the map view the whole of the body (466 by 256, 233 by 128 tiles at 2 pixels a tile); on a screen too small to hold
 * it, the whole screen, the view the room left of the body (the phone layout). Known places is a panel over the view,
 * not part of the layout. Pure, so the window and its tests share the numbers.
 */

/**
 * The window's size on a screen that holds it.
 */
export const WINDOW_SIZE: Size = Object.freeze({ width: 480, height: 300 });

const PADDING = 3;
const FOOTER = KIT_METRICS.window.footer.top + 16 + KIT_METRICS.window.footer.bottom;
const FRAME_RULE = 1;

/**
 * How the window sits on a screen.
 */
export interface WorldMapLayout {
  readonly phone: boolean;
  readonly window: Size;
  readonly left: number;
  readonly top: number;
  readonly padding: number;
  readonly view: Size;
}

/**
 * What the window takes beyond its view on a screen that holds it: its frame, header, rule and padding.
 */
export const MAP_CHROME: Size = Object.freeze({ width: FRAMES.window.insets.left + FRAMES.window.insets.right + 2 * PADDING, height: FRAMES.window.insets.top + FRAMES.window.insets.bottom + KIT_METRICS.window.header + FRAME_RULE + 2 * PADDING });

/**
 * Brings a wished window size into what the window can be: no smaller than its default, no larger than the field the
 * `nearby` crop can cover allows (ADR-100 decision 45) or than the screen's room holds, the default winning where the
 * room is smaller.
 *
 * @param area The screen in logical pixels.
 * @param wish The size asked for.
 * @returns A whole size.
 */
function windowSize(area: Size, wish: Size): Size {
  const room = windowRoom(area);
  const most = mapWindowMost(MAP_CHROME);
  const width = Math.max(WINDOW_SIZE.width, Math.min(Math.floor(wish.width), most.width, room.right - room.left));
  const height = Math.max(WINDOW_SIZE.height, Math.min(Math.floor(wish.height), most.height, room.bottom - room.top));
  return { width, height };
}

/**
 * Lays the window out on a screen: the desktop window when the screen holds it, else the whole screen.
 *
 * @param area The screen in logical pixels.
 * @param wish The size the player has given the window, when it has one; the default size when omitted, and ignored in
 *   the phone layout.
 * @returns The layout.
 */
export function worldMapLayout(area: Size, wish: Size | null = null): WorldMapLayout {
  const phone = area.width < WINDOW_SIZE.width || area.height < WINDOW_SIZE.height;
  const window: Size = phone ? { width: area.width, height: area.height } : wish === null ? WINDOW_SIZE : windowSize(area, wish);
  const insets = FRAMES.window.insets;
  const bodyWidth = window.width - insets.left - insets.right - 2 * PADDING;
  const bodyHeight = window.height - insets.top - insets.bottom - KIT_METRICS.window.header - FRAME_RULE - 2 * PADDING - (phone ? FOOTER : 0);
  const view: Size = { width: bodyWidth, height: bodyHeight };
  return { phone, window, left: phone ? 0 : centreOffset(area.width, window.width), top: phone ? 0 : centreOffset(area.height, window.height), padding: PADDING, view };
}

/**
 * The places panel's width in logical pixels: the card's 152 for the list, now a panel over the map.
 */
export const PANEL_WIDTH = 152;

/**
 * How far in from the view's edges the Places button and the panel lie.
 */
export const PANEL_INSET = 4;

/**
 * The room the filter field takes in the panel when it is shown: the field and a gap under it.
 */
export const FILTER_STRIP = KIT_METRICS.field.boxHeight + 4;

/**
 * Where the places panel lies in the view.
 */
export interface PanelBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly list: number;
}

/**
 * The places panel's box: at the view's top left under the Places button, as wide as `PANEL_WIDTH` or the view less its
 * insets, as tall as the room under the button, and a scrolling list of what is left under its heading and filter.
 *
 * @param view The view's size.
 * @param filter Whether the filter field is shown.
 * @param start The panel's top in the view: under the Places button unless given (the float's strip holds the button, so
 *   its panel starts under the strip).
 * @returns The box; the list's height is the viewport the panel draws its rows for.
 */
export function panelBox(view: Size, filter: boolean, start: number = PANEL_INSET + KIT_METRICS.button.height + 2): PanelBox {
  const top = start;
  const height = Math.max(0, view.height - top - PANEL_INSET);
  return { left: PANEL_INSET, top, width: Math.min(PANEL_WIDTH, Math.max(0, view.width - 2 * PANEL_INSET)), height, list: Math.max(0, height - KIT_METRICS.list.heading - (filter ? FILTER_STRIP : 0)) };
}

/**
 * The part of a 2D context the view's painter uses; `S` is what the picture is drawn from.
 */
export interface ViewContext<S> {
  imageSmoothingEnabled: boolean;
  fillStyle: string | CanvasGradient | CanvasPattern;
  clearRect(x: number, y: number, width: number, height: number): void;
  fillRect(x: number, y: number, width: number, height: number): void;
  drawImage(source: S, sx: number, sy: number, sw: number, sh: number, dx: number, dy: number, dw: number, dh: number): void;
}

/**
 * Paints the view: the part of the picture it shows, one picture pixel to a logical pixel and never smoothed, the rest
 * of the view clear. The marks are the window's own elements over it.
 *
 * @param context The view canvas's context.
 * @param picture What the map's picture is drawn from, or null before it exists.
 * @param view The view's size.
 * @param origin The picture pixel at the view's top left corner; negative where the picture is smaller than the view.
 * @param size The picture's size.
 */
export function paintView<S>(context: ViewContext<S>, picture: S | null, view: Size, origin: { readonly left: number; readonly top: number }, size: Size): void {
  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, view.width, view.height);
  if (picture === null) {
    return;
  }
  const sx = Math.max(0, origin.left);
  const sy = Math.max(0, origin.top);
  const width = Math.min(size.width, origin.left + view.width) - sx;
  const height = Math.min(size.height, origin.top + view.height) - sy;
  if (width <= 0 || height <= 0) {
    return;
  }
  context.drawImage(picture, sx, sy, width, height, sx - origin.left, sy - origin.top, width, height);
}
